import React, { useState } from 'react';
import { PublicHoliday } from '@openmirror/shared';

interface PublicHolidaysSettingsProps {
  value: PublicHoliday[];
  onChange: (holidays: PublicHoliday[]) => void;
}

const PublicHolidaysSettings: React.FC<PublicHolidaysSettingsProps> = ({ value, onChange }) => {
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addHoliday = () => {
    if (!date) {
      setError('Choose a holiday date.');
      return;
    }
    if (value.some(holiday => holiday.date === date)) {
      setError('That date is already listed.');
      return;
    }
    onChange([...value, { date, name: name.trim() }].sort((left, right) => left.date.localeCompare(right.date)));
    setDate('');
    setName('');
    setError(null);
  };

  return (
    <section aria-labelledby="public-holidays-title" className="border-t border-gray-200 pt-6 space-y-3">
      <h4 id="public-holidays-title" className="text-sm font-medium text-gray-900">Public holidays</h4>
      <p className="text-sm text-gray-500">
        These dates are closed all day in your company timezone, including overnight hours.
        Your after-hours prompt applies on these dates. Add each year’s dates, including any
        observed holidays, then select Save Changes below.
      </p>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div>
          <label htmlFor="public-holiday-date" className="block text-sm font-medium text-gray-700">Date</label>
          <input
            id="public-holiday-date"
            type="date"
            value={date}
            onChange={event => { setDate(event.target.value); setError(null); }}
            className="mt-1 block w-full rounded-md border border-gray-300 p-2 text-sm"
          />
        </div>
        <div className="flex-1">
          <label htmlFor="public-holiday-name" className="block text-sm font-medium text-gray-700">Name (optional)</label>
          <input
            id="public-holiday-name"
            type="text"
            value={name}
            onChange={event => setName(event.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 p-2 text-sm"
          />
        </div>
        <button type="button" onClick={addHoliday} className="rounded-md border border-indigo-600 px-3 py-2 text-sm font-medium text-indigo-600">
          Add holiday
        </button>
      </div>
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      {value.length === 0 ? (
        <p className="text-sm text-gray-500">No public holidays configured.</p>
      ) : (
        <ul className="divide-y divide-gray-200">
          {value.map(holiday => (
            <li key={holiday.date} className="flex items-center justify-between gap-3 py-2 text-sm">
              <div className="min-w-0 break-words">
                <time dateTime={holiday.date} className="font-medium">{holiday.date}</time>
                {holiday.name && <span className="ml-2 text-gray-600">{holiday.name}</span>}
              </div>
              <button
                type="button"
                aria-label={`Remove holiday ${holiday.date}`}
                onClick={() => onChange(value.filter(entry => entry.date !== holiday.date))}
                className="shrink-0 text-red-600 hover:text-red-800"
              >Remove</button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default PublicHolidaysSettings;
